import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import MatchCard from "@/components/MatchCard";
import { saveMatchNav } from "@/components/MatchNavArrows";
import { ChevronLeft, ChevronRight, Search, X, Loader2, Calendar, Star, SlidersHorizontal, Trophy, TrendingUp, Shield, RefreshCw, Radio, Globe, Activity, Clock, FlaskConical } from "lucide-react";
import PlayProfilePanel, {
  loadActiveProfile, saveActiveProfile,
  loadProfileEnabled, saveProfileEnabled,
  computeProfileScore, getProfile,
} from "@/components/PlayProfilePanel";
import PublicBrainLabModal from "@/components/PublicBrainLabModal";
import AiPicksBanner from "@/components/AiPicksBanner";
import MultiMonolitePanel from "@/components/MultiMonolitePanel";
import { usePremium } from "@/contexts/AuthContext";
import { PremiumLockChip } from "@/components/PremiumGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = {
  bg: "var(--bc-bg, #020202)", surface: "var(--bc-panel, #09090B)", highlight: "var(--bc-panel-elev, #141416)",
  border: "var(--bc-border, rgba(255,255,255,0.07))", text: "var(--bc-ink, #FFFFFF)", muted: "var(--bc-sub, #71717A)",
  green: "#22C55E",  // semantic only: LIVE indicator, success outcomes
  accent: "var(--bc-accent)",                     // brand-tunable
  accentSoft: "var(--bc-accent-soft)",            // 10% bg
  accentSoft2: "var(--bc-accent-soft-2)",         // 18% bg
  accentMedium: "var(--bc-accent-medium)",        // 32% borders
  cyan: "#06B6D4", blue: "#3B82F6", yellow: "#F59E0B", red: "#EF4444",
};
const PRIORITY_COUNTRIES = ["Italy", "England", "Spain", "Germany", "France", "Portugal", "Netherlands", "Belgium", "Turkey", "Brazil", "Argentina", "USA"];

// Pure function: Top Picks score bonus based on league prominence
function getLeaguePriorityBonus(leagueName) {
  const n = (leagueName || "").toLowerCase();
  const major = ["premier league", "la liga", "serie a", "bundesliga", "ligue 1", "champions league", "europa league", "eredivisie", "primeira liga", "super lig", "championship"];
  const minor = ["women", "femm", "u19", "u21", "u23", "u17", "lowland", "juniors", "amateur", "reserve", "regional", "u18", "u16"];
  if (major.some(k => n.includes(k))) return 35;
  if (minor.some(k => n.includes(k))) return -25;
  return 0;
}
const EUROPEAN_COUNTRIES = ["Italy", "England", "Spain", "Germany", "France", "Portugal", "Netherlands", "Belgium", "Turkey", "Scotland", "Austria", "Switzerland", "Greece", "Czech-Republic", "Poland", "Denmark", "Sweden", "Norway", "Croatia", "Serbia", "Romania", "Ukraine", "Russia", "Hungary", "Bulgaria", "Slovakia", "Slovenia", "Finland", "Iceland", "Ireland", "Northern-Ireland", "Wales", "Bosnia", "Albania", "Montenegro", "North-Macedonia", "Kosovo", "Luxembourg", "Malta", "Cyprus", "Georgia", "Armenia", "Azerbaijan", "Estonia", "Latvia", "Lithuania", "Moldova", "Belarus"];
const FAV_KEY = "xgp_favorites";
const STATUS_FILTERS = [
  { key: "all", label: "Tutte" },
  { key: "NS", label: "Da Giocare" },
  { key: "LIVE", label: "Live", statuses: ["1H", "HT", "2H", "ET", "P", "BT"] },
  { key: "FT", label: "Finite", statuses: ["FT", "AET", "PEN"] },
];

// Top 5 + Champions/Europa League IDs (API-Football)
const TOP_LEAGUE_IDS = [39, 140, 135, 78, 61, 2, 3, 88, 94, 203, 207, 45, 143, 79, 140, 333];

// League tier: 1 = top flight, 2 = second division, 3 = lower/youth/women (hidden by default)
const SECONDARY_KEYWORDS = [
  "primavera", "women", "u17", "u18", "u19", "u20", "u21", "u23",
  "reserve", "youth", "amateur", "serie d", "serie c",
  "tercera", "regional", "national 2", "national 3", "national 4",
  "league two", "league one", "3. liga", "regionalliga",
  "ligue 2", "tweede", "segunda b", "serie b2",
  "4th division", "5th division",
  "femminile", "femenino", "féminin", "frauen",
  "cup", "coppa", "coupe", "pokal", "super cup", "trofeo",
  "playoff", "play-off"
];
const TIER2_KEYWORDS = [
  "serie b", "championship", "2. bundesliga", "segunda", "ligue 2",
  "eerste divisie", "segunda liga", "serie b", "super lig",
  "2nd division", "division 2", "league two", "league one"
];

function getLeagueTier(leagueName) {
  const lower = (leagueName || "").toLowerCase();
  for (const kw of SECONDARY_KEYWORDS) {
    if (lower.includes(kw)) return 3;
  }
  for (const kw of TIER2_KEYWORDS) {
    if (lower.includes(kw)) return 2;
  }
  return 1;
}

// Mini SVG sparkline: running accuracy trend for a league
function LeagueSparkline({ sparkline }) {
  if (!sparkline || sparkline.n < 3) return null;
  const { running, accuracy_pct, n } = sparkline;
  const W = 48, H = 16, PAD = 2;
  const toX = (i) => PAD + (i / Math.max(n - 1, 1)) * (W - 2 * PAD);
  const toY = (v) => PAD + (1 - v / 100) * (H - 2 * PAD);
  const pathD = running.map((v, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)},${toY(v).toFixed(1)}`).join(" ");
  const last = running[n - 1];
  const color = last >= 60 ? "#22C55E" : last >= 40 ? "#F59E0B" : "#EF4444";
  return (
    <div className="flex items-center gap-1" title={`Accuratezza modello su questa lega: ${accuracy_pct}% (ultimi ${n} pronostici completati)`}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible", flexShrink: 0 }}>
        <line x1={PAD} y1={toY(50)} x2={W - PAD} y2={toY(50)}
          stroke="var(--bc-border, rgba(255,255,255,0.07))" strokeWidth={0.5} strokeDasharray="2,2" />
        <path d={pathD} fill="none" stroke={color} strokeWidth={1.5}
          strokeLinecap="round" strokeLinejoin="round" opacity={0.75} />
        <circle cx={toX(n - 1)} cy={toY(last)} r={2.5} fill={color} />
      </svg>
      <span style={{ fontSize: "0.6rem", color, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
        {accuracy_pct}%
      </span>
    </div>
  );
}

function formatDate(d) {
  return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
}
function toISODate(d) { return d.toISOString().split("T")[0]; }

// Helper: convert "1-3" → "1_3" (multigol band format)
const _bandKey = (b) => (b ? String(b).replace("-", "_") : "");

const PRED_FILTERS = [
  { key: "all", label: "Tutti", check: () => true, sortField: null },
  // ── 1X2 secchi ────────────────────────────────────────────────
  { key: "1",  label: "1", check: (p) => (p?.combined?.home || 0) > 50,   sortField: (p) => p?.combined?.home || 0 },
  { key: "x",  label: "X", check: (p) => (p?.combined?.draw || 0) > 30,   sortField: (p) => p?.combined?.draw || 0 },
  { key: "2",  label: "2", check: (p) => (p?.combined?.away || 0) > 50,   sortField: (p) => p?.combined?.away || 0 },
  // ── Doppia Chance ─────────────────────────────────────────────
  { key: "1x", label: "1X", check: (p) => (p?.combined?.double_1x || 0) > 65, sortField: (p) => p?.combined?.double_1x || 0 },
  { key: "x2", label: "X2", check: (p) => (p?.combined?.double_x2 || 0) > 65, sortField: (p) => p?.combined?.double_x2 || 0 },
  { key: "12", label: "12", check: (p) => (p?.combined?.double_12 || 0) > 65, sortField: (p) => p?.combined?.double_12 || 0 },
  // ── Gol Match ─────────────────────────────────────────────────
  { key: "over15",  label: "Over 1.5",  check: (p) => (p?.combined?.over15  || 0) > 65, sortField: (p) => p?.combined?.over15  || 0 },
  { key: "over25",  label: "Over 2.5",  check: (p) => (p?.combined?.over25  || 0) > 55, sortField: (p) => p?.combined?.over25  || 0 },
  { key: "under15", label: "Under 1.5", check: (p) => (p?.combined?.under15 ?? (100 - (p?.combined?.over15 || 0))) > 55, sortField: (p) => p?.combined?.under15 ?? (100 - (p?.combined?.over15 || 0)) },
  { key: "under25", label: "Under 2.5", check: (p) => (p?.combined?.under25 || 0) > 55, sortField: (p) => p?.combined?.under25 || 0 },
  { key: "under35", label: "Under 3.5", check: (p) => (p?.combined?.under35 || 0) > 65, sortField: (p) => p?.combined?.under35 || 0 },
  { key: "gol",     label: "Gol",       check: (p) => (p?.combined?.btts_yes || 0) > 55, sortField: (p) => p?.combined?.btts_yes || 0 },
  { key: "nogol",   label: "NoGol",     check: (p) => (p?.combined?.btts_no  || 0) > 55, sortField: (p) => p?.combined?.btts_no  || 0 },
  // ── Multigol ──────────────────────────────────────────────────
  { key: "mg13", label: "Multigol 1-3", check: (p) => (p?.multigol?.mg_1_3 || 0) > 45, sortField: (p) => p?.multigol?.mg_1_3 || 0 },
  { key: "mg24", label: "Multigol 2-4", check: (p) => (p?.multigol?.mg_2_4 || 0) > 45, sortField: (p) => p?.multigol?.mg_2_4 || 0 },
  // Multigol Casa/Trasferta — band dinamica per match
  {
    key: "mg_casa",
    label: "Multigol Casa",
    dynamicLabel: (p) => p?.multigol?.home_best_band ? `MG Casa ${p.multigol.home_best_band}` : "Multigol Casa",
    check: (p) => !!p?.multigol?.home_best_band,
    sortField: (p) => p?.multigol?.[`home_${_bandKey(p?.multigol?.home_best_band)}`] || 0,
  },
  {
    key: "mg_trasferta",
    label: "Multigol Trasferta",
    dynamicLabel: (p) => p?.multigol?.away_best_band ? `MG Trasferta ${p.multigol.away_best_band}` : "Multigol Trasferta",
    check: (p) => !!p?.multigol?.away_best_band,
    sortField: (p) => p?.multigol?.[`away_${_bandKey(p?.multigol?.away_best_band)}`] || 0,
  },
  // ── Tempi (HT / ST) ───────────────────────────────────────────
  { key: "ht_over05", label: "Over 0.5 HT", check: (p) => (p?.goals_by_half?.first_half?.over_05  || 0) > 60, sortField: (p) => p?.goals_by_half?.first_half?.over_05  || 0 },
  { key: "ht_over15", label: "Over 1.5 HT", check: (p) => (p?.goals_by_half?.first_half?.over_15  || 0) > 40, sortField: (p) => p?.goals_by_half?.first_half?.over_15  || 0 },
  { key: "st_over05", label: "Over 0.5 ST", check: (p) => (p?.goals_by_half?.second_half?.over_05 || 0) > 60, sortField: (p) => p?.goals_by_half?.second_half?.over_05 || 0 },
  { key: "st_over15", label: "Over 1.5 ST", check: (p) => (p?.goals_by_half?.second_half?.over_15 || 0) > 40, sortField: (p) => p?.goals_by_half?.second_half?.over_15 || 0 },
  // ── Speciali ──────────────────────────────────────────────────
  { key: "hyper",  label: "HyperCombo",   check: (p) => !!(p?.hyper_combo?.combo || p?.hyper_combo?.market), sortField: (p) => p?.hyper_combo?.prob || 0 },
  { key: "gundam", label: "Gundam Score", check: (p) => !!(p?.gundam_scores?.length), sortField: (p) => (p?.gundam_scores || []).slice(0, 3).reduce((acc, s) => acc + (s?.prob || 0), 0) },
];

// Cluster groups for the filter panel — same order users see in the UI
const FILTER_GROUPS = [
  { label: "1X2",            keys: ["1", "x", "2"] },
  { label: "Doppia Chance",  keys: ["1x", "x2", "12"] },
  { label: "Gol Match",      keys: ["over15", "over25", "under15", "under25", "under35", "gol", "nogol"] },
  { label: "Multigol",       keys: ["mg13", "mg24", "mg_casa", "mg_trasferta"] },
  { label: "Tempi",          keys: ["ht_over05", "ht_over15", "st_over05", "st_over15"] },
  { label: "Speciali",       keys: ["hyper", "gundam"] },
];

// Mutually exclusive filter pairs — if both are selected in cross mode we
// surface a red warning chip so the user doesn't end up with an empty list.
const CONFLICT_PAIRS = [
  ["over15", "under15"], ["over25", "under25"], ["over25", "under15"],
  ["gol", "nogol"],
  ["1", "x"], ["1", "2"], ["x", "2"],
  ["1x", "x2"], // intersezione = solo "X"
  ["over15", "nogol"], // poco senso pratico
];

const findConflicts = (keys) => {
  if (!keys || keys.length < 2) return [];
  const set = new Set(keys);
  return CONFLICT_PAIRS.filter(([a, b]) => set.has(a) && set.has(b));
};

// Geometric-mean score for a match across the given cross filters.
// Returns ``null`` when the geo-mean is below ``minp`` (match excluded).
// IMPORTANT: ``minp`` is applied on the **cross-score** (the geometric
// mean of the legs), NOT per single leg. Questo permette ai preset
// scoperti dal backtest (es. ``2+X2+NoGol >= 55%``) di funzionare
// correttamente: anche se uno dei filtri ha singolarmente prob bassa,
// purché la media geometrica superi ``minp`` la partita è inclusa.
const computeCrossScore = (pred, keys, minp) => {
  if (!keys?.length) return null;
  const probs = keys.map((k) => {
    const f = PRED_FILTERS.find((x) => x.key === k);
    const v = f?.sortField ? f.sortField(pred) : 0;
    return Number(v) || 0;
  });
  if (probs.some((p) => p <= 0)) return null;
  const prod = probs.reduce((acc, p) => acc * p, 1);
  const score = Math.pow(prod, 1 / probs.length);
  if (score < minp) return null;
  return score;
};

export default function HomePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FAV_KEY) || "[]"); } catch { return []; }
  });
  const [predData, setPredData] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [leagueSparklines, setLeagueSparklines] = useState({});
  const [leagueSearch, setLeagueSearch] = useState("");
  const navigate = useNavigate();

  // ── Tutti i filtri/stato navigazione sono nell'URL ──────────────────────────
  // Helper: aggiorna un singolo param preservando gli altri
  const setParam = useCallback((key, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value === null || value === undefined || value === "") {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Lettura dai param URL con fallback ai default
  const dateStr = searchParams.get("date") || toISODate(new Date());
  const date = useMemo(() => {
    const d = new Date(dateStr + "T12:00:00");
    return isNaN(d.getTime()) ? new Date() : d;
  }, [dateStr]);

  const selectedCountry = searchParams.get("country") || "Tutti";
  const selectedLeagueId = parseInt(searchParams.get("league") || "0") || 0;
  const sortBy = searchParams.get("sort") || "default";
  const predFilter = searchParams.get("pred") || "all";
  const valueOnly = searchParams.get("vb") === "1";
  const vbSort = searchParams.get("vbs") || "edge"; // "edge" | "prob"
  const statusFilter = searchParams.get("status") || "all";
  const search = searchParams.get("q") || "";

  // ── Cross-filter (multi-select up to 3 markets) ─────────────────────────────
  // URL: ?cross=key1,key2,key3 and ?minp=NN (minimum % threshold).
  // The intersection lists matches where ALL selected market probs ≥ minp,
  // sorted by geometric mean of those probs.
  const crossKeys = useMemo(() => {
    const raw = searchParams.get("cross") || "";
    return raw ? raw.split(",").filter(Boolean).slice(0, 3) : [];
  }, [searchParams]);
  const crossActive = crossKeys.length > 0;
  const crossMin = parseInt(searchParams.get("minp") || "50") || 50;
  const setCrossKeys = useCallback((arr) => {
    setParam("cross", arr && arr.length ? arr.join(",") : null);
  }, [setParam]);
  const setCrossMin = useCallback((v) => {
    const n = Math.max(0, Math.min(99, parseInt(v) || 0));
    setParam("minp", n === 50 ? null : String(n));
  }, [setParam]);
  const toggleCrossKey = useCallback((k) => {
    const next = crossKeys.includes(k)
      ? crossKeys.filter((x) => x !== k)
      : (crossKeys.length >= 3 ? crossKeys : [...crossKeys, k]);
    setCrossKeys(next);
  }, [crossKeys, setCrossKeys]);

  // Saved filter presets (localStorage)
  const [savedPresets, setSavedPresets] = useState(() => {
    try { return JSON.parse(localStorage.getItem("bc_saved_presets") || "[]"); }
    catch { return []; }
  });
  const persistPresets = useCallback((arr) => {
    setSavedPresets(arr);
    try { localStorage.setItem("bc_saved_presets", JSON.stringify(arr)); }
    catch { /* ignore quota */ }
  }, []);
  const [brainLabOpen, setBrainLabOpen] = useState(false);
  const reloadPresetsFromStorage = useCallback(() => {
    try {
      const arr = JSON.parse(localStorage.getItem("bc_saved_presets") || "[]");
      setSavedPresets(arr);
    } catch { /* ignore */ }
  }, []);

  // ── Freemium gating: certi controlli sono Premium-only ───────────
  const { isPremium } = usePremium();
  const guardPremium = useCallback((label = "Funzionalità Premium") => {
    if (isPremium) return true;
    navigate("/pricing");
    return false;
  }, [isPremium, navigate]);
  const PREMIUM_FILTER_KEYS = new Set(["gundam"]);

  // ── Profilo di Gioco — selettore + sort tachimetro ─────────────
  // Stato condiviso con la MatchPage tramite localStorage (stessa key).
  const [playProfile, setPlayProfile] = useState(() => loadActiveProfile());
  const [profileEnabled, setProfileEnabled] = useState(() => loadProfileEnabled());
  const onProfileChange = useCallback((id) => {
    setPlayProfile(id);
    saveActiveProfile(id);
  }, []);
  const onProfileToggle = useCallback((enabled) => {
    setProfileEnabled(enabled);
    saveProfileEnabled(enabled);
  }, []);

  // Setter che aggiornano l'URL
  const setDate = useCallback((d) => setParam("date", toISODate(d)), [setParam]);
  const setSelectedCountry = useCallback((c) => setParam("country", c === "Tutti" ? null : c), [setParam]);
  const setSelectedLeague = useCallback((id) => setParam("league", id === 0 ? null : id), [setParam]);
  const setSortBy = useCallback((v) => setParam("sort", v === "default" ? null : v), [setParam]);
  const setPredFilter = useCallback((v) => setParam("pred", v === "all" ? null : v), [setParam]);
  const setValueOnly = useCallback((v) => {
    setParam("vb", v ? "1" : null);
    if (!v) setParam("vbs", null); // reset sort when disabling
  }, [setParam]);
  const setVbSort = useCallback((v) => setParam("vbs", v === "edge" ? null : v), [setParam]);
  const setStatusFilter = useCallback((v) => setParam("status", v === "all" ? null : v), [setParam]);
  const setSearch = useCallback((v) => setParam("q", v || null), [setParam]);

  // Quick Criteria: preset (URL param) + sliders (localStorage)
  const activePreset = searchParams.get("preset") || "";
  const setActivePreset = useCallback((v) => setParam("preset", v || null), [setParam]);
  const [minAff, setMinAff] = useState(() => parseInt(localStorage.getItem("bc_minAff") || "0"));
  const [minProb, setMinProb] = useState(() => parseInt(localStorage.getItem("bc_minProb") || "0"));
  const handleMinAff = useCallback((v) => { const n = parseInt(v); setMinAff(n); localStorage.setItem("bc_minAff", n); }, []);
  const handleMinProb = useCallback((v) => { const n = parseInt(v); setMinProb(n); localStorage.setItem("bc_minProb", n); }, []);
  const applyPreset = useCallback((key) => {
    if (activePreset === key) {
      setActivePreset(null);
      if (key === "sicure") { handleMinAff(0); handleMinProb(0); }
    } else {
      setActivePreset(key);
      if (key === "sicure") { handleMinAff(70); handleMinProb(55); }
    }
  }, [activePreset, setActivePreset, handleMinAff, handleMinProb]);
  const resetQC = useCallback(() => { setActivePreset(null); handleMinAff(0); handleMinProb(0); }, [setActivePreset, handleMinAff, handleMinProb]);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (!isRefresh) { setLoading(true); setError(null); }
    try {
      const [fixtRes, predRes] = await Promise.all([
        axios.get(`${API}/fixtures/today?date=${dateStr}`),
        axios.get(`${API}/predictions-summary?date=${dateStr}`).catch(() => ({ data: { predictions: {} } }))
      ]);
      setData(fixtRes.data);
      setPredData(predRes.data.predictions || {});

      // Trigger batch solo se <50% predictions calcolate OPPURE se alcuni match mancano di value_data
      // (non usare book_odds come trigger: molti tipi di bet non hanno quote disponibili in API)
      const predCount = Object.keys(predRes.data.predictions || {}).length;
      const totalMatches = fixtRes.data.total || 0;
      const anyMissingValueData = predCount > 0 && Object.values(predRes.data.predictions || {})
        .some(p => p.target_kitano?.bet && p.target_kitano.bet !== "NO BET"
          && (!p.value_data || Object.keys(p.value_data).length === 0));
      if ((predCount < totalMatches * 0.5 || anyMissingValueData) && !isRefresh) {
        setBatchLoading(true);
        axios.post(`${API}/predictions/batch?date=${dateStr}`).then(() => {
          return axios.get(`${API}/predictions-summary?date=${dateStr}`);
        }).then((res) => {
          setPredData(res.data.predictions || {});
        }).catch(() => {}).finally(() => setBatchLoading(false));
      }
    } catch { if (!isRefresh) setError("Errore nel caricamento. Riprova."); }
    finally { if (!isRefresh) setLoading(false); }
  }, [dateStr]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh LIVE every 60s
  useEffect(() => {
    if (!data) return;
    const hasLive = data.leagues.some((l) => l.matches.some((m) => ["1H", "HT", "2H", "ET", "P", "BT"].includes(m.status)));
    if (!hasLive) return;
    const interval = setInterval(() => fetchData(true), 60000);
    return () => clearInterval(interval);
  }, [data, fetchData]);

  const toggleFavorite = useCallback((leagueId, leagueName, country) => {
    setFavorites((prev) => {
      const exists = prev.some((f) => f.id === leagueId);
      const next = exists ? prev.filter((f) => f.id !== leagueId) : [...prev, { id: leagueId, name: leagueName, country }];
      localStorage.setItem(FAV_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const isFavorite = (leagueId) => favorites.some((f) => f.id === leagueId);

  const countries = useMemo(() => {
    if (!data) return [];
    const set = new Set(data.leagues.map((l) => l.country).filter(Boolean));
    const all = Array.from(set);
    const priority = PRIORITY_COUNTRIES.filter((c) => all.includes(c));
    const rest = all.filter((c) => !PRIORITY_COUNTRIES.includes(c)).sort();
    return ["Tutti", "Favoriti", ...priority, ...rest];
  }, [data]);

  const enrichedLeagues = useMemo(() => {
    if (!data) return [];
    return data.leagues.map((l) => ({
      ...l,
      tier: getLeagueTier(l.name),
      matches: l.matches.map((m) => {
        const pred = predData[String(m.id)];
        // Badge now comes directly from backend (based on Target Kitano)
        const badge = pred?.badge || null;
        return { ...m, pred, badge };
      }),
    }));
  }, [data, predData]);

  const filteredLeagues = useMemo(() => {
    let leagues = enrichedLeagues;

    // League selector filter (takes priority over country filter)
    if (selectedLeagueId) {
      leagues = leagues.filter((l) => l.id === selectedLeagueId);
    } else {
      // Country filter
      if (selectedCountry === "Favoriti") leagues = leagues.filter((l) => isFavorite(l.id));
      else if (selectedCountry !== "Tutti") leagues = leagues.filter((l) => l.country === selectedCountry);
      // Note: backend whitelist already handles league filtering — no secondary frontend filter needed

      if (selectedCountry !== "Tutti" && selectedCountry !== "Favoriti") {
        leagues = [...leagues].sort((a, b) => a.tier - b.tier);
      }
    }

    leagues = leagues.map((l) => ({
      ...l,
      matches: l.matches.filter((m) => {
        if (statusFilter !== "all") {
          const sf = STATUS_FILTERS.find((f) => f.key === statusFilter);
          const allowedStatuses = sf?.statuses || [sf?.key];
          if (!allowedStatuses.includes(m.status)) return false;
        }
        const searchOk = !search || m.home_team.name.toLowerCase().includes(search.toLowerCase()) || m.away_team.name.toLowerCase().includes(search.toLowerCase());
        if (!searchOk) return false;

        // Market filter — single (legacy) OR cross (multi-AND with threshold)
        if (crossActive) {
          const score = computeCrossScore(m.pred, crossKeys, crossMin);
          if (score === null) return false;
        } else if (predFilter !== "all") {
          const pf = PRED_FILTERS.find((f) => f.key === predFilter);
          if (pf?.check && !pf.check(m.pred)) return false;
        }

        // Value bet overlay (combinabile con qualsiasi filtro di mercato)
        if (valueOnly) {
          const bets = m.pred?.recommended_bets || [];
          const tk = m.pred?.target_kitano;
          const hasValueForMarket = bets.some(b => b.has_value) || !!tk?.has_value;
          if (!hasValueForMarket) return false;
        }

        return true;
      }),
    })).filter((l) => l.matches.length > 0);
    return leagues;
  }, [enrichedLeagues, selectedCountry, selectedLeagueId, search, predFilter, valueOnly, statusFilter, favorites, crossActive, crossKeys, crossMin]);

  // Quick Criteria filtering layer — applied on top of filteredLeagues
  const qcFilteredLeagues = useMemo(() => {
    const needsFilter = minAff > 0 || minProb > 0 || (activePreset && activePreset !== "sicure");
    if (!needsFilter) return filteredLeagues;
    return filteredLeagues
      .filter(l => activePreset === "top5" ? TOP_LEAGUE_IDS.includes(l.id) : true)
      .map(l => ({
        ...l,
        matches: l.matches.filter(m => {
          if (minAff > 0 && (m.pred?.affidabilita?.score ?? 0) < minAff) return false;
          if (minProb > 0 && (m.pred?.target_kitano?.prob ?? 0) < minProb) return false;
          if (activePreset === "value") {
            const hv = m.pred?.target_kitano?.has_value || (m.pred?.recommended_bets || []).some(b => b.has_value);
            if (!hv) return false;
          }
          if (activePreset === "ora") {
            const diff = (new Date(m.date) - Date.now()) / 60000;
            if (diff < -30 || diff > 120) return false;
          }
          return true;
        }),
      }))
      .filter(l => l.matches.length > 0);
  }, [filteredLeagues, minAff, minProb, activePreset]);

  // Sort: flat list when sorting by risk/confidence OR when predFilter has a sortField OR valueOnly is active
  const sortedContent = useMemo(() => {
    const activePredFilter = PRED_FILTERS.find((f) => f.key === predFilter);
    const hasPredSort = predFilter !== "all" && activePredFilter?.sortField;

    // Cross-filter takes precedence over single-market sort
    if (crossActive) {
      const allMatches = qcFilteredLeagues.flatMap((l) =>
        l.matches.map((m) => ({
          ...m, leagueName: l.name, leagueCountry: l.country, leagueFlag: l.flag, leagueLogo: l.logo,
          _crossInfo: {
            keys: crossKeys,
            probs: crossKeys.map((k) => {
              const f = PRED_FILTERS.find((x) => x.key === k);
              return f?.sortField ? Number(f.sortField(m.pred)) || 0 : 0;
            }),
            score: computeCrossScore(m.pred, crossKeys, crossMin) || 0,
          },
        }))
      );
      allMatches.sort((a, b) => (b._crossInfo.score || 0) - (a._crossInfo.score || 0));
      const labelParts = crossKeys.map((k) => PRED_FILTERS.find((x) => x.key === k)?.label || k);
      return {
        type: "flat", matches: allMatches,
        sortLabel: `Cross: ${labelParts.join(" ∩ ")} · ≥${crossMin}%`,
        isCross: true,
      };
    }

    if (sortBy === "default" && !hasPredSort && !valueOnly && !profileEnabled) return { type: "grouped", leagues: qcFilteredLeagues };

    const allMatches = qcFilteredLeagues.flatMap((l) => l.matches.map((m) => ({ ...m, leagueName: l.name, leagueCountry: l.country, leagueFlag: l.flag, leagueLogo: l.logo })));

    // ── Profilo di Gioco attivo → ordina per voto Tachimetro del profilo
    // selezionato. Le partite senza ``pred`` finiscono in fondo.
    if (profileEnabled && !hasPredSort && !valueOnly && sortBy === "default") {
      const scored = allMatches.map((m) => {
        const r = m.pred ? computeProfileScore(playProfile, m.pred) : { score: -1 };
        return { ...m, _profileScore: r.score, _profileBase: r.baseScore || 0 };
      });
      scored.sort((a, b) => {
        if ((b._profileScore ?? -1) !== (a._profileScore ?? -1)) {
          return (b._profileScore ?? -1) - (a._profileScore ?? -1);
        }
        return new Date(a.date || 0) - new Date(b.date || 0);
      });
      const prof = getProfile(playProfile);
      return {
        type: "flat",
        matches: scored,
        sortLabel: `Profilo: ${prof.label} · Tachimetro`,
        isProfile: true,
      };
    }

    // Value bet overlay: sort by edge (value_pct) or by model probability
    if (valueOnly) {
      // Collect every "value candidate" for a match:
      //   - Target Kitano (if it itself is a value bet), plus
      //   - every recommended_bet where has_value === true
      // We then pick the best candidate for the active sort key (prob | edge).
      // This gives a single, precise, deterministic ranking value per match.
      const pickBestCandidate = (pred) => {
        if (!pred) return null;
        const tk = pred.target_kitano || {};
        const bets = pred.recommended_bets || [];
        const candidates = [];
        if (tk.has_value && typeof tk.prob === "number") {
          candidates.push({
            bet: tk.bet, type: tk.type,
            prob: Number(tk.prob) || 0,
            value_pct: Number(tk.value_pct) || 0,
            source: "tk",
          });
        }
        for (const b of bets) {
          if (!b || !b.has_value) continue;
          candidates.push({
            bet: b.bet, type: b.type,
            prob: Number(b.prob) || 0,
            value_pct: Number(b.value_pct) || 0,
            source: "bet",
          });
        }
        if (candidates.length === 0) return null;
        // Dedup by identical bet name (TK and a recommended bet often coincide)
        const seen = new Set();
        const unique = candidates.filter((c) => {
          const k = `${c.type}|${c.bet}`;
          if (seen.has(k)) return false;
          seen.add(k); return true;
        });
        const key = vbSort === "prob" ? "prob" : "value_pct";
        // Primary sort by chosen metric, tie-break by the OTHER metric
        // (so a match with 76% prob + 14 edge beats one with 76% + 10 edge).
        unique.sort((a, b) => {
          if (b[key] !== a[key]) return b[key] - a[key];
          const alt = key === "prob" ? "value_pct" : "prob";
          return b[alt] - a[alt];
        });
        return unique[0];
      };

      const getSortKey = (pred) => {
        const best = pickBestCandidate(pred);
        if (!best) return { primary: -1, secondary: -1 };
        return vbSort === "prob"
          ? { primary: best.prob, secondary: best.value_pct }
          : { primary: best.value_pct, secondary: best.prob };
      };

      const sortLabel = predFilter !== "all" ? `${activePredFilter?.label} · Value Bet` : "Value Bet";
      allMatches.sort((a, b) => {
        const ka = getSortKey(a.pred), kb = getSortKey(b.pred);
        if (kb.primary !== ka.primary) return kb.primary - ka.primary;
        if (kb.secondary !== ka.secondary) return kb.secondary - ka.secondary;
        // Final deterministic tie-breaker: fixture id
        return Number(a.id || 0) - Number(b.id || 0);
      });
      // Attach the "best candidate" so the card can highlight exactly WHY
      // this match landed in its current position.
      const enriched = allMatches.map((m) => ({
        ...m, _bestValue: pickBestCandidate(m.pred),
      }));
      return { type: "flat", matches: enriched, sortLabel, isValue: true };
    }

    if (hasPredSort) {
      // Sort by prediction probability for selected market
      const sortFn = activePredFilter.sortField;
      allMatches.sort((a, b) => (sortFn(b.pred) || 0) - (sortFn(a.pred) || 0));
      return { type: "flat", matches: allMatches, sortLabel: activePredFilter.label };
    }

    const withPred = allMatches.filter((m) => m.pred);
    const withoutPred = allMatches.filter((m) => !m.pred);
    if (sortBy === "risk_desc") withPred.sort((a, b) => (b.pred?.affidabilita?.score || b.pred?.risk?.score || 0) - (a.pred?.affidabilita?.score || a.pred?.risk?.score || 0));
    else if (sortBy === "risk_asc") withPred.sort((a, b) => (a.pred?.affidabilita?.score || a.pred?.risk?.score || 0) - (b.pred?.affidabilita?.score || b.pred?.risk?.score || 0));
    else if (sortBy === "confidence") withPred.sort((a, b) => (b.pred?.confidence || 0) - (a.pred?.confidence || 0));
    else if (sortBy === "mg_conf") {
      // Multi-factor Multigol confidence — uses max(home_confidence, away_confidence)
      // as the pooled score, matches with no multifactor yet (older cache)
      // sink to the bottom.
      const score = (p) => {
        const mg = p?.multigol || {};
        const hc = mg.home_confidence;
        const ac = mg.away_confidence;
        if (hc == null && ac == null) return -1;
        return Math.max(hc || 0, ac || 0);
      };
      withPred.sort((a, b) => score(b.pred) - score(a.pred));
    }
    else if (sortBy === "dna") {
      // Match DNA strength — ordina i match per quanto convergente è il
      // verdetto storico delle 50 partite gemelle. Match senza DNA cached
      // (-1) finiscono in coda.
      const dna = (p) => (typeof p?.match_dna_score === "number" ? p.match_dna_score : -1);
      withPred.sort((a, b) => dna(b.pred) - dna(a.pred));
      const all = [...withPred, ...withoutPred];
      return { type: "flat", matches: all, sortLabel: "Match DNA · convergenza storica" };
    }
    else if (sortBy === "time_asc") {
      const all = [...withPred, ...withoutPred];
      all.sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
      return { type: "flat", matches: all, sortLabel: "Orario ↑", isTime: true };
    }
    else if (sortBy === "time_desc") {
      const all = [...withPred, ...withoutPred];
      all.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      return { type: "flat", matches: all, sortLabel: "Orario ↓", isTime: true };
    }
    return { type: "flat", matches: [...withPred, ...withoutPred] };
  }, [qcFilteredLeagues, sortBy, predFilter, valueOnly, vbSort, crossActive, crossKeys, crossMin, profileEnabled, playProfile]);

  const analyzedCount = Object.keys(predData).length;
  const isToday = dateStr === toISODate(new Date());
  const liveCount = data ? data.leagues.reduce((c, l) => c + l.matches.filter((m) => ["1H", "HT", "2H", "ET", "P", "BT"].includes(m.status)).length, 0) : 0;
  const totalAllMatches = data ? data.leagues.reduce((c, l) => c + l.matches.length, 0) : 0;

  // ── Build the flat ordered list of fixture IDs the user is actually seeing ─
  // Used by MatchNavArrows to enable "previous/next match" navigation.
  const orderedFixtureIds = useMemo(() => {
    if (sortedContent.type === "flat") {
      return (sortedContent.matches || []).map((m) => Number(m.id));
    }
    // grouped by league — flatten in display order
    return (sortedContent.leagues || []).flatMap(
      (l) => (l.matches || []).map((m) => Number(m.id)),
    );
  }, [sortedContent]);

  // Navigate to a match while memorising scroll + the ordered sibling list
  const goToMatch = useCallback((matchId) => {
    try {
      sessionStorage.setItem("bc_home_scroll", String(window.scrollY || 0));
      sessionStorage.setItem("bc_home_date", dateStr || "");
    } catch {}
    const idx = orderedFixtureIds.indexOf(Number(matchId));
    saveMatchNav(orderedFixtureIds, idx >= 0 ? idx : 0);
    navigate(`/match/${matchId}`);
  }, [orderedFixtureIds, navigate, dateStr]);

  // Keep MatchNavArrows in sync with the LIVE HomePage order: ogni volta
  // che cambia ``orderedFixtureIds`` (sort, profilo, filtri, refresh dati,
  // cross-filter…) ri-salviamo la lista. Se ``nav`` non esiste ancora,
  // non facciamo nulla. Se esiste, aggiorniamo gli ids preservando, dove
  // possibile, il match correntemente visualizzato. Così le frecce
  // prev/next non saltano mai partite e seguono l'ordine attuale.
  useEffect(() => {
    if (!orderedFixtureIds || orderedFixtureIds.length === 0) return;
    try {
      const raw = sessionStorage.getItem("bc_match_nav");
      const prev = raw ? JSON.parse(raw) : null;
      const prevCurrentId = prev?.ids?.[prev?.currentIdx];
      const newIdx = prevCurrentId != null
        ? orderedFixtureIds.indexOf(Number(prevCurrentId))
        : -1;
      sessionStorage.setItem(
        "bc_match_nav",
        JSON.stringify({
          ids: orderedFixtureIds,
          currentIdx: newIdx >= 0 ? newIdx : 0,
        }),
      );
    } catch { /* ignore quota / parse errors */ }
  }, [orderedFixtureIds]);

  // Restore scroll position after content loads (returning from a match)
  useEffect(() => {
    if (loading || !data) return;
    let raf = 0;
    const savedDate = sessionStorage.getItem("bc_home_date") || "";
    const saved = parseInt(sessionStorage.getItem("bc_home_scroll") || "0", 10);
    if (saved > 0 && savedDate === dateStr) {
      // Wait one frame so that the just-mounted content has its real height
      raf = requestAnimationFrame(() => {
        window.scrollTo({ top: saved, behavior: "instant" });
        sessionStorage.removeItem("bc_home_scroll");
      });
    }
    return () => cancelAnimationFrame(raf);
  }, [loading, data, dateStr]);

  // Leagues for the visual selector — sorted by importance (tier + priority country)
  const sortedLeaguesForSelector = useMemo(() => {
    if (!enrichedLeagues) return [];
    return [...enrichedLeagues]
      .filter((l) => l.matches.filter((m) => m.status === "NS" || ["1H","HT","2H","ET","P"].includes(m.status)).length > 0)
      .sort((a, b) => {
        if (a.tier !== b.tier) return a.tier - b.tier;
        const ai = PRIORITY_COUNTRIES.indexOf(a.country);
        const bi = PRIORITY_COUNTRIES.indexOf(b.country);
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return b.matches.length - a.matches.length;
      });
  }, [enrichedLeagues]);

  // Match Juice: Top 5 "juicy" matches of the day.
  // Score = reliability 40% + convergence 30% + pick strength 20% + value bonus.
  // Each pick shown must have book/fair odds ≥ 1.25 (we walk down the
  // recommended_bets list until we find a qualifying one).
  const topPicksJuice = useMemo(() => {
    if (!data || analyzedCount === 0) return [];
    const MIN_ODDS = 1.25;
    const all = enrichedLeagues.flatMap((l) =>
      l.matches.flatMap((m) => {
        if (!m.pred || m.status !== "NS") return [];
        if (m.pred?.target_kitano?.type === "no_bet") return [];
        // Walk recommended_bets to find the best bet with odds ≥ 1.25
        const bets = m.pred?.recommended_bets || [];
        const validBet = bets.find((b) => {
          if (!b?.prob || b.type === "no_bet") return false;
          const impliedFair = 100 / Math.max(b.prob, 1);
          const odds = b.book_odds || impliedFair;
          return odds >= MIN_ODDS;
        });
        if (!validBet) return [];
        const aff = m.pred?.affidabilita?.score || 0;
        const conv = m.pred?.convergence?.score || 50;
        const pickProb = Math.min(validBet.prob || 0, 80); // cap at 80 for the juice component
        const valueBonus = validBet.has_value ? 20 : 0;
        const leagueBonus = getLeaguePriorityBonus(l.name) * 0.15; // soft hint
        const juice = Math.round(
          aff * 0.4 + conv * 0.3 + pickProb * 0.125 + valueBonus + leagueBonus
        );
        return [{
          ...m,
          leagueName: l.name, leagueCountry: l.country,
          leagueFlag: l.flag, leagueLogo: l.logo, leagueId: l.id,
          juice: Math.max(0, Math.min(100, juice)),
          juiceBet: validBet,
        }];
      })
    );
    return all.sort((a, b) => b.juice - a.juice).slice(0, 5);
  }, [enrichedLeagues, data, analyzedCount]);

  // Fetch league accuracy sparklines once top picks are resolved
  useEffect(() => {
    const allPicks = topPicksJuice;
    const ids = [...new Set(allPicks.map((m) => m.leagueId).filter(Boolean))];
    if (ids.length === 0) return;
    axios.get(`${API}/league-sparklines?league_ids=${ids.join(",")}`)
      .then((res) => setLeagueSparklines(res.data))
      .catch(() => {});
  }, [topPicksJuice]);

  return (
    <div style={{ background: C.bg, minHeight: "100vh" }} className="grid-bg">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4">
        <AiPicksBanner />
      </div>

      {/* ─── HERO SECTION ───────────────────────────────────── */}
      <div className="relative overflow-hidden" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-7xl mx-auto px-4 pt-8 pb-6">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Activity size={13} style={{ color: "var(--bc-accent)" }} />
                <span className="font-data text-xs uppercase tracking-[0.28em]" style={{ color: "var(--bc-accent)" }}>
                  ANALISI CALCISTICA INTELLIGENTE
                </span>
              </div>
              <h1 className="font-heading font-black uppercase" style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)", letterSpacing: "0.06em", color: C.text, lineHeight: 1.05 }}>
                Benvenuto in{" "}
                <span style={{ color: "var(--bc-accent)", textShadow: "0 0 14px var(--bc-glow)" }}>BET CRACKER</span>
              </h1>
              {!loading && data && (
                <p className="font-data text-sm mt-2" style={{ color: C.muted }}>
                  {new Date(date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
                  {" — "}
                  <strong style={{ color: C.text }}>{totalAllMatches} partite</strong> in programma
                  {liveCount > 0 && (
                    <span className="ml-2 font-data text-xs px-2 py-0.5" style={{ color: C.green, background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.2)" }}>
                      <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 animate-live align-middle" style={{ background: C.green }} />
                      {liveCount} LIVE
                    </span>
                  )}
                </p>
              )}
            </div>
            {analyzedCount > 0 && (
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="text-right">
                  <div className="font-data text-2xl font-bold tabular-nums" style={{ color: "var(--bc-accent)" }}>{analyzedCount}</div>
                  <div className="font-data text-xs" style={{ color: C.muted }}>partite analizzate</div>
                </div>
              </div>
            )}
          </div>

          {/* League selector */}
          {!loading && sortedLeaguesForSelector.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center gap-3 mb-3">
                <span className="font-data text-xs uppercase tracking-wider flex-shrink-0" style={{ color: C.muted }}>Campionato:</span>
                <div className="relative flex-1 max-w-[180px]">
                  <input
                    data-testid="league-search-input"
                    type="text"
                    value={leagueSearch}
                    onChange={(e) => setLeagueSearch(e.target.value)}
                    placeholder="Cerca campionato…"
                    className="w-full pl-2 pr-6 py-1 text-xs font-heading outline-none"
                    style={{
                      background: C.highlight,
                      border: `1px solid ${leagueSearch ? "var(--bc-accent-medium)" : C.border}`,
                      color: leagueSearch ? C.text : C.muted,
                    }}
                  />
                  {leagueSearch && (
                    <button
                      onClick={() => setLeagueSearch("")}
                      data-testid="league-search-clear"
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-xs leading-none"
                      style={{ color: C.muted }}
                    >✕</button>
                  )}
                </div>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                {/* Tutti button */}
                <button
                  data-testid="league-selector-all"
                  onClick={() => { setSelectedLeague(0); setLeagueSearch(""); }}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 transition-all duration-150"
                  style={{
                    background: !selectedLeagueId ? C.accentSoft2 : C.highlight,
                    border: `1px solid ${!selectedLeagueId ? "var(--bc-accent-medium)" : C.border}`,
                    color: !selectedLeagueId ? C.accent : C.muted,
                  }}
                >
                  <span className="font-heading font-bold text-xs uppercase tracking-wider">Tutti</span>
                  <span className="font-data text-[10px] px-1 py-0.5" style={{ background: !selectedLeagueId ? C.accentSoft2 : "var(--bc-border, rgba(255,255,255,0.07))", color: !selectedLeagueId ? C.accent : C.muted }}>
                    {totalAllMatches}
                  </span>
                </button>
                {sortedLeaguesForSelector
                  .filter((l) => !leagueSearch || l.name.toLowerCase().includes(leagueSearch.toLowerCase()) || l.country.toLowerCase().includes(leagueSearch.toLowerCase()))
                  .map((l) => {
                  const isActive = selectedLeagueId === l.id;
                  const activeColor = l.tier === 1 ? C.accent : l.tier === 2 ? C.cyan : C.muted;
                  return (
                    <button
                      key={l.id}
                      data-testid={`league-selector-${l.id}`}
                      onClick={() => { setSelectedLeague(l.id); setLeagueSearch(""); }}
                      className="flex-shrink-0 flex items-center gap-2 px-3 py-2 transition-all duration-150"
                      style={{
                        background: isActive ? (l.tier === 1 ? C.accentSoft : "rgba(6,182,212,0.1)") : C.highlight,
                        border: `1px solid ${isActive ? activeColor + "55" : C.border}`,
                        minWidth: 0,
                      }}
                    >
                      {l.logo && (
                        <img src={l.logo} alt="" className="w-4 h-4 object-contain flex-shrink-0"
                          onError={(e) => e.target.style.display = "none"} />
                      )}
                      {l.flag && !l.logo && (
                        <img src={l.flag} alt="" className="w-4 h-3 object-cover flex-shrink-0"
                          onError={(e) => e.target.style.display = "none"} />
                      )}
                      <span className="font-heading font-bold text-xs uppercase tracking-tight whitespace-nowrap"
                        style={{ color: isActive ? activeColor : "var(--bc-ink, #D4D4D8)" }}>
                        {l.name.length > 18 ? l.name.slice(0, 16) + "…" : l.name}
                      </span>
                      <span className="font-data text-[10px] px-1 flex-shrink-0"
                        style={{ background: "var(--bc-border, rgba(255,255,255,0.07))", color: C.muted }}>
                        {l.matches.length}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Date Navigation */}
      <div style={{ background: "var(--bc-panel, rgba(9,9,11,0.95))", borderBottom: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <button data-testid="prev-date-btn" onClick={() => { const d = new Date(date); d.setDate(d.getDate() - 1); setDate(d); }}
            className="p-2 transition-colors hover:text-white" style={{ color: C.muted }}>
            <ChevronLeft size={18} />
          </button>
          <div className="flex items-center gap-3">
            <Calendar size={14} style={{ color: C.accent }} />
            <span className="font-heading text-sm uppercase tracking-widest" style={{ color: C.text }}>
              {isToday ? "OGGI — " : ""}{formatDate(date).toUpperCase()}
            </span>
            {data && <span className="font-data text-xs px-2 py-0.5" style={{ background: C.highlight, color: C.muted }}>{data.total}</span>}
          </div>
          <button data-testid="next-date-btn" onClick={() => { const d = new Date(date); d.setDate(d.getDate() + 1); setDate(d); }}
            className="p-2 transition-colors hover:text-white" style={{ color: C.muted }}>
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* MULTIMONOLITE — owner only: pick di giornata sopra soglia */}
      <MultiMonolitePanel date={toISODate(date)} />

      {/* Profilo di Gioco · selettore compatto + tachimetro adattivo */}
      <div className="max-w-7xl mx-auto px-4 pt-4">
        <PlayProfilePanel
          activeProfile={playProfile}
          onChange={onProfileChange}
          enabled={profileEnabled}
          onToggleEnabled={onProfileToggle}
          compact={true}
          predictions={null}
          aggregateScore={profileEnabled ? (() => {
            const preds = Object.values(predData || {});
            if (!preds.length) return 0;
            const scores = preds.map((p) => computeProfileScore(playProfile, p).score);
            return scores.reduce((a, b) => a + b, 0) / scores.length;
          })() : null}
          aggregateLabel={
            (() => {
              const n = Object.keys(predData || {}).length;
              return n
                ? `Media su ${n} partite analizzate · le partite sono ordinate dal tachimetro più alto al più basso.`
                : "Caricamento predizioni in corso…";
            })()
          }
        />
      </div>

      {/* Search + Country Filter */}
      <div className="max-w-7xl mx-auto px-4 pt-4 pb-2 flex flex-col gap-3">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: C.muted }} />
            <input data-testid="search-input" type="text" placeholder="Cerca squadra..." value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-8 py-2 text-sm outline-none font-data"
              style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: "0.8rem" }} />
            {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2"><X size={12} style={{ color: C.muted }} /></button>}
          </div>
          <button onClick={() => setShowFilters((v) => !v)} data-testid="toggle-filters-btn"
            className="flex items-center gap-2 px-4 py-2.5 transition-all hover:brightness-110"
            style={{
              background: showFilters ? C.accent : C.accentSoft,
              color: showFilters ? "#09090B" : C.accent,
              border: `1.5px solid ${C.accent}`,
              boxShadow: showFilters ? `0 0 12px ${C.accent}66` : `0 0 6px ${C.accent}33`,
            }}>
            <SlidersHorizontal size={15} />
            <span className="font-heading text-sm uppercase font-extrabold tracking-wider">Filtri</span>
            {(() => {
              const activeCount = (predFilter !== "all" ? 1 : 0) + (valueOnly ? 1 : 0) + (sortBy !== "default" ? 1 : 0) + (crossActive ? 1 : 0);
              return activeCount > 0 ? (
                <span className="font-data text-[11px] font-bold px-1.5 py-0.5 rounded-sm animate-pulse"
                  style={{ background: showFilters ? "rgba(0,0,0,0.25)" : C.accent, color: showFilters ? "#09090B" : "#09090B" }}>
                  {activeCount}
                </span>
              ) : null;
            })()}
          </button>
        </div>

        {/* Active filters summary strip — sempre visibile quando ci sono filtri attivi */}
        {(predFilter !== "all" || valueOnly || sortBy !== "default" || crossActive) && (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="active-filters-strip">
            <span className="font-data text-[10px] uppercase tracking-widest" style={{ color: C.muted }}>Attivi:</span>
            {crossActive && (
              <button data-testid="active-filter-cross" onClick={() => setCrossKeys([])}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-heading uppercase font-bold transition-colors hover:brightness-110"
                style={{ background: "rgba(167,139,250,0.1)", color: "#A78BFA", border: "1px solid rgba(167,139,250,0.4)" }}>
                Cross: {crossKeys.map((k) => PRED_FILTERS.find((f) => f.key === k)?.label || k).join(" ∩ ")} · ≥{crossMin}%
                <X size={10} />
              </button>
            )}
            {sortBy !== "default" && (() => {
              const sortLabel = sortBy === "risk_desc" ? "Affidabilità ↓"
                : sortBy === "risk_asc" ? "Affidabilità ↑"
                : sortBy === "time_asc" ? "Orario ↑"
                : sortBy === "time_desc" ? "Orario ↓"
                : sortBy === "dna" ? "Match DNA 🧬" : sortBy;
              return (
                <button data-testid="active-filter-sort" onClick={() => setSortBy("default")}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-heading uppercase font-bold transition-colors hover:brightness-110"
                  style={{ background: C.surface, color: C.cyan, border: `1px solid ${C.cyan}55` }}>
                  Ordina: {sortLabel}
                  <X size={10} />
                </button>
              );
            })()}
            {predFilter !== "all" && (() => {
              const pf = PRED_FILTERS.find(f => f.key === predFilter);
              return (
                <button data-testid="active-filter-market" onClick={() => setPredFilter("all")}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-heading uppercase font-bold transition-colors hover:brightness-110"
                  style={{ background: C.surface, color: C.blue, border: `1px solid ${C.blue}55` }}>
                  Mercato: {pf?.label || predFilter}
                  <X size={10} />
                </button>
              );
            })()}
            {valueOnly && (
              <button data-testid="active-filter-value" onClick={() => setValueOnly(false)}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-heading uppercase font-bold transition-colors hover:brightness-110"
                style={{ background: C.surface, color: C.accent, border: `1px solid ${C.accent}55` }}>
                ★ Value Bet ({vbSort === "prob" ? "Prob" : "Edge"})
                <X size={10} />
              </button>
            )}
            <button data-testid="clear-all-filters" onClick={() => { setPredFilter("all"); setValueOnly(false); setSortBy("default"); }}
              className="px-2 py-0.5 text-[10px] font-heading uppercase font-bold ml-1 transition-colors hover:brightness-125"
              style={{ background: "transparent", color: C.muted, border: `1px dashed ${C.border}` }}>
              Reset
            </button>
          </div>
        )}

        {/* Country Filter */}
        <div className="flex items-center gap-2">
          <div className="flex gap-2 overflow-x-auto pb-1 flex-1" style={{ scrollbarWidth: "none" }}>
            {countries.map((c) => (
              <button key={c} data-testid={`filter-${c}`} onClick={() => setSelectedCountry(c)}
                className="whitespace-nowrap px-3 py-1.5 text-xs font-heading uppercase tracking-wider transition-all duration-150 flex items-center gap-1.5"
                style={{ background: selectedCountry === c ? C.accent : C.surface, color: selectedCountry === c ? "#09090B" : C.muted,
                  border: `1px solid ${selectedCountry === c ? C.accent : C.border}`, fontWeight: 700 }}>
                {c === "Favoriti" && <Star size={10} fill={selectedCountry === "Favoriti" ? "#09090B" : C.yellow} style={{ color: selectedCountry === "Favoriti" ? "#09090B" : C.yellow }} />}
                {c}
                {c === "Favoriti" && favorites.length > 0 && <span style={{ opacity: 0.8 }}>({favorites.length})</span>}
              </button>
            ))}
          </div>
          <button
            data-testid="sort-time-btn"
            onClick={() => setSortBy(sortBy === "default" ? "time_asc" : sortBy === "time_asc" ? "time_desc" : "default")}
            title={sortBy === "time_asc" ? "Orario crescente — clicca per decrescente" : sortBy === "time_desc" ? "Orario decrescente — clicca per disattivare" : "Ordina per orario"}
            className="flex items-center justify-center gap-1 px-2 py-2 flex-shrink-0 transition-all duration-150 font-data text-xs font-bold"
            style={{
              background: (sortBy === "time_asc" || sortBy === "time_desc") ? C.cyan : "rgba(6,182,212,0.08)",
              color: (sortBy === "time_asc" || sortBy === "time_desc") ? "#020202" : C.cyan,
              border: `1px solid ${(sortBy === "time_asc" || sortBy === "time_desc") ? C.cyan : "rgba(6,182,212,0.3)"}`,
            }}>
            <Clock size={13} />
            {sortBy === "time_asc" && "↑"}
            {sortBy === "time_desc" && "↓"}
          </button>
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-2 flex-wrap">
          {STATUS_FILTERS.map(({ key, label }) => (
            <button key={key} data-testid={`status-${key}`} onClick={() => setStatusFilter(key)}
              className="px-3 py-1.5 text-xs font-heading uppercase tracking-wider font-bold transition-all duration-150 flex items-center gap-1.5"
              style={{ background: statusFilter === key ? (key === "LIVE" ? C.green : C.blue) : C.surface,
                color: statusFilter === key ? "#09090B" : C.muted,
                border: `1px solid ${statusFilter === key ? (key === "LIVE" ? C.green : C.blue) : C.border}` }}>
              {key === "LIVE" && <Radio size={10} style={{ color: statusFilter === key ? "#09090B" : C.green }} />}
              {label}
            </button>
          ))}
          {liveCount > 0 && (
            <span className="font-data text-xs flex items-center gap-1" style={{ color: C.green }}>
              <span className="inline-block w-1.5 h-1.5 rounded-full animate-live" style={{ background: C.green }} />
              {liveCount} live
            </span>
          )}
          {batchLoading && (
            <span className="font-data text-xs flex items-center gap-1.5 ml-auto" style={{ color: C.yellow }}>
              <Loader2 size={11} className="animate-spin" />
              Analisi in corso...
            </span>
          )}
        </div>

        {/* Sort + Pred Filter (collapsible) */}
        {showFilters && (
          <div className="flex flex-col gap-3 p-4 animate-fade-in" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
            {/* ── ORDINA ───────────────────────────────────────────────── */}
            <div className="flex items-start gap-3 flex-wrap">
              <span className="font-heading text-[11px] uppercase tracking-widest font-extrabold flex items-center gap-1.5 flex-shrink-0 pt-1"
                style={{ color: C.cyan, minWidth: "80px" }}
                title="Affidabilità: score 0-100 della convergenza dei modelli (Poisson + ELO + Forma) — ALTA / MEDIA / BASSA / NO BET">
                Ordina <span className="text-[10px] opacity-60">ⓘ</span>
              </span>
              <div className="flex items-center gap-2 flex-wrap flex-1">
                {[["default", "Predefinito"], ["risk_desc", "Affidabilità ↓"], ["risk_asc", "Affidabilità ↑"], ["dna", "Match DNA 🧬"]].map(([k, l]) => (
                  <button key={k} onClick={() => setSortBy(k)} data-testid={`sort-${k}`}
                    className="px-3 py-1.5 text-xs font-heading uppercase font-bold transition-all hover:brightness-110"
                    style={{
                      background: sortBy === k ? (k === "dna" ? "#22FFA3" : C.cyan) : C.highlight,
                      color: sortBy === k ? "#020202" : C.muted,
                      border: `1px solid ${sortBy === k ? (k === "dna" ? "#22FFA3" : C.cyan) : C.border}`,
                      boxShadow: sortBy === k && k === "dna" ? "0 0 12px rgba(34,255,163,0.4)" : "none",
                    }}>
                    {l}
                  </button>
                ))}
                {sortBy !== "default" && analyzedCount === 0 && (
                  <span className="font-data text-xs" style={{ color: C.yellow }}>Analizza partite per attivare l'ordinamento</span>
                )}
              </div>
            </div>

            <div className="h-px" style={{ background: C.border }} />

            {/* ── CROSS-FILTER (multi-select fino a 3) ─────────────────── */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-heading text-[11px] uppercase tracking-widest font-extrabold"
                  style={{ color: "#A78BFA", minWidth: "80px" }}>
                  Cross
                </span>
                <button
                  data-testid="cross-filter-toggle"
                  onClick={() => {
                    if (!guardPremium("Cross Filter")) return;
                    setCrossKeys(crossActive ? [] : [crossKeys[0] || "over15"]);
                  }}
                  className="px-3 py-1.5 text-xs font-heading uppercase font-bold transition-all hover:brightness-110"
                  style={{
                    background: crossActive ? "#A78BFA" : "rgba(167,139,250,0.1)",
                    color: crossActive ? "#020202" : "#A78BFA",
                    border: `1.5px solid ${crossActive ? "#A78BFA" : "rgba(167,139,250,0.4)"}`,
                    boxShadow: crossActive ? "0 0 8px rgba(167,139,250,0.5)" : "none",
                  }}>
                  ⨉ {crossActive ? `CROSS ATTIVO (${crossKeys.length}/3)` : "Attiva Cross Filter"}
                  {!isPremium && <PremiumLockChip />}
                </button>
                {crossActive && (
                  <>
                    <div className="flex items-center gap-1.5">
                      <span className="font-data text-[10px] uppercase tracking-wider" style={{ color: C.muted }}>
                        Soglia min.
                      </span>
                      <input
                        type="number"
                        min="0" max="99" step="5"
                        value={crossMin}
                        onChange={(e) => setCrossMin(e.target.value)}
                        data-testid="cross-min-input"
                        className="font-data text-xs px-2 py-1 w-16 text-right tabular-nums"
                        style={{
                          background: C.surface, color: C.text,
                          border: `1px solid rgba(167,139,250,0.4)`,
                          outline: "none",
                        }}
                      />
                      <span className="font-data text-xs" style={{ color: "#A78BFA" }}>%</span>
                    </div>
                    <button
                      onClick={() => setCrossKeys([])}
                      data-testid="cross-clear"
                      className="font-data text-[10px] uppercase tracking-wider px-2 py-1"
                      style={{ color: C.muted, border: `1px solid ${C.border}` }}>
                      Cancella
                    </button>
                  </>
                )}
                {/* BRAIN LAB — modal con pattern scoperti */}
                <button
                  data-testid="open-brainlab-btn"
                  onClick={() => {
                    if (!guardPremium("Brain Lab")) return;
                    setBrainLabOpen(true);
                  }}
                  className="ml-auto px-3 py-1.5 text-xs font-heading uppercase font-bold transition-all hover:brightness-110 inline-flex items-center gap-1.5"
                  style={{
                    background: "rgba(34,211,238,0.12)",
                    color: "#22D3EE",
                    border: "1.5px solid rgba(34,211,238,0.5)",
                    boxShadow: "0 0 10px rgba(34,211,238,0.25)",
                    letterSpacing: "0.08em",
                  }}>
                  <FlaskConical size={12} />
                  BrainLab
                  {!isPremium && <PremiumLockChip />}
                </button>
              </div>
              {/* Conflict warning */}
              {(() => {
                const conflicts = findConflicts(crossKeys);
                if (!conflicts.length) return null;
                return (
                  <div className="flex items-center gap-2 px-3 py-2"
                    data-testid="cross-conflict-warning"
                    style={{
                      background: "rgba(239,68,68,0.08)",
                      border: "1px solid rgba(239,68,68,0.4)",
                      color: "#FCA5A5",
                    }}>
                    <span className="font-data text-[11px]">⚠</span>
                    <span className="font-data text-[10px] uppercase tracking-wider">
                      Filtri in conflitto: {conflicts.map(([a, b]) => {
                        const la = PRED_FILTERS.find((f) => f.key === a)?.label || a;
                        const lb = PRED_FILTERS.find((f) => f.key === b)?.label || b;
                        return `${la} ↔ ${lb}`;
                      }).join(" · ")}. Probabilmente otterrai 0 partite.
                    </span>
                  </div>
                );
              })()}
              {/* Saved presets */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-data text-[10px] uppercase tracking-wider" style={{ color: C.muted, minWidth: "80px" }}>
                  Filtri salvati
                </span>
                {savedPresets.length === 0 && (
                  <span className="font-data text-[10px]" style={{ color: C.muted, opacity: 0.6 }}>
                    Nessun preset — usa "Salva combinazione"
                  </span>
                )}
                {savedPresets.map((p) => (
                  <span key={p.name} className="flex items-center"
                    data-testid={`saved-preset-${p.name}`}>
                    <button
                      onClick={() => {
                        // Apply ALL URL params in a single setSearchParams call.
                        // Chaining multiple setParam() calls breaks because
                        // React Router gives each callback the same committed
                        // ``prev`` snapshot — only the last write would persist.
                        setSearchParams((prev) => {
                          const next = new URLSearchParams(prev);
                          const cross = p.cross && p.cross.length ? p.cross.join(",") : null;
                          cross ? next.set("cross", cross) : next.delete("cross");
                          const minp = parseInt(p.minp || 50);
                          minp === 50 ? next.delete("minp") : next.set("minp", String(minp));
                          const pred = p.pred || "all";
                          pred === "all" ? next.delete("pred") : next.set("pred", pred);
                          p.vb ? next.set("vb", "1") : next.delete("vb");
                          return next;
                        }, { replace: true });
                      }}
                      className="px-2.5 py-1 font-data text-[10px] uppercase tracking-wider"
                      style={{
                        background: "rgba(167,139,250,0.1)",
                        color: "#A78BFA",
                        border: "1px solid rgba(167,139,250,0.4)",
                      }}>
                      {p.name}
                    </button>
                    <button
                      onClick={() => persistPresets(savedPresets.filter((x) => x.name !== p.name))}
                      title="Elimina"
                      className="px-1.5 py-1 font-data text-[10px]"
                      style={{
                        color: "#FCA5A5",
                        background: "rgba(239,68,68,0.08)",
                        borderTop: "1px solid rgba(239,68,68,0.3)",
                        borderRight: "1px solid rgba(239,68,68,0.3)",
                        borderBottom: "1px solid rgba(239,68,68,0.3)",
                      }}>
                      ×
                    </button>
                  </span>
                ))}
                {crossActive && (
                  <button
                    data-testid="save-preset-btn"
                    onClick={() => {
                      const name = window.prompt("Nome del preset:");
                      if (!name) return;
                      const trimmed = name.trim().slice(0, 24);
                      if (!trimmed) return;
                      const next = [
                        ...savedPresets.filter((x) => x.name !== trimmed),
                        { name: trimmed, cross: crossKeys, minp: crossMin, pred: predFilter, vb: valueOnly },
                      ];
                      persistPresets(next);
                    }}
                    className="px-2.5 py-1 font-data text-[10px] uppercase tracking-wider"
                    style={{
                      background: "rgba(34,197,94,0.1)",
                      color: C.green,
                      border: `1px solid rgba(34,197,94,0.4)`,
                    }}>
                    + Salva combinazione
                  </button>
                )}
              </div>
            </div>

            <div className="h-px" style={{ background: C.border }} />

            {/* ── MERCATO (raggruppato in cluster) ────────────────────── */}
            <div className="flex flex-col gap-2.5">
              <div className="flex items-center gap-2">
                <span className="font-heading text-[11px] uppercase tracking-widest font-extrabold" style={{ color: C.blue }}>
                  Mercato {crossActive && <span className="opacity-60">· multi-select</span>}
                </span>
                {!crossActive && (
                  <button onClick={() => setPredFilter("all")} data-testid="pred-filter-all"
                    className="px-2.5 py-1 text-[11px] font-heading uppercase font-bold transition-all hover:brightness-110"
                    style={{
                      background: predFilter === "all" ? C.blue : C.highlight,
                      color: predFilter === "all" ? "#fff" : C.muted,
                      border: `1px solid ${predFilter === "all" ? C.blue : C.border}`,
                    }}>
                    Tutti
                  </button>
                )}
              </div>
              {FILTER_GROUPS.map(({ label, keys }) => (
                <div key={label} className="flex items-center gap-2 flex-wrap">
                  <span className="font-data text-[10px] uppercase tracking-wider flex-shrink-0"
                    style={{ color: C.muted, minWidth: "100px" }}>
                    {label}
                  </span>
                  <div className="flex items-center gap-1.5 flex-wrap flex-1">
                    {keys.map((k) => {
                      const f = PRED_FILTERS.find((x) => x.key === k);
                      if (!f) return null;
                      const isCross = crossKeys.includes(k);
                      const isActive = crossActive ? isCross : predFilter === k;
                      const tone = crossActive ? "#A78BFA" : C.blue;
                      const fullCross = crossActive && !isCross && crossKeys.length >= 3;
                      const isPremiumChip = PREMIUM_FILTER_KEYS.has(k);
                      const gated = isPremiumChip && !isPremium;
                      return (
                        <button key={k}
                          onClick={() => {
                            if (gated) { navigate("/pricing"); return; }
                            crossActive ? toggleCrossKey(k) : setPredFilter(k);
                          }}
                          disabled={fullCross}
                          data-testid={`pred-filter-${k}`}
                          className="px-2.5 py-1 text-[11px] font-heading uppercase font-bold transition-all hover:brightness-110 inline-flex items-center gap-1"
                          style={{
                            background: isActive ? tone : C.highlight,
                            color: isActive ? "#fff" : C.muted,
                            border: `1px solid ${isActive ? tone : C.border}`,
                            opacity: fullCross ? 0.35 : 1,
                            cursor: fullCross ? "not-allowed" : "pointer",
                          }}>
                          {crossActive && isCross && <span className="mr-1">✓</span>}
                          {f.label}
                          {gated && <PremiumLockChip />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="h-px" style={{ background: C.border }} />

            {/* ── VALUE BET ────────────────────────────────────────────── */}
            <div className="flex items-start gap-3 flex-wrap">
              <span className="font-heading text-[11px] uppercase tracking-widest font-extrabold flex-shrink-0 pt-1"
                style={{ color: C.accent, minWidth: "80px" }}>
                Value
              </span>
              <div className="flex items-center gap-2 flex-wrap flex-1">
                <button
                  data-testid="value-only-toggle"
                  onClick={() => {
                    if (!guardPremium("Value Bet")) return;
                    setValueOnly(!valueOnly);
                  }}
                  className="px-3 py-1.5 text-xs font-heading uppercase font-bold transition-all flex items-center gap-1.5 hover:brightness-110"
                  style={{
                    background: valueOnly ? C.accent : C.accentSoft,
                    color: valueOnly ? "#09090B" : C.accent,
                    border: `1.5px solid ${valueOnly ? C.accent : "var(--bc-accent-medium)"}`,
                    boxShadow: valueOnly ? `0 0 8px ${C.accent}66` : "none",
                  }}>
                  ★ {valueOnly ? "VALUE BET ATTIVO" : "Filtra Value Bet"}
                  {!isPremium && <PremiumLockChip />}
                </button>
                {valueOnly && (
                  <div className="flex items-center gap-1">
                    <span className="font-data text-[10px] uppercase tracking-wider mr-1" style={{ color: C.muted }}>Ordina:</span>
                    {[["edge", "Edge ↓"], ["prob", "Probabilità ↓"]].map(([k, l]) => (
                      <button key={k} data-testid={`vb-sort-${k}`}
                        onClick={() => setVbSort(k)}
                        className="px-2 py-0.5 text-[10px] font-heading uppercase font-bold transition-all hover:brightness-110"
                        style={{
                          background: vbSort === k ? C.accent : C.accentSoft,
                          color: vbSort === k ? "#09090B" : C.accent,
                          border: `1px solid ${vbSort === k ? C.accent : "var(--bc-accent-medium)"}`,
                        }}>
                        {l}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {(predFilter !== "all" || valueOnly) && sortedContent.type === "flat" && (
              <p className="font-data text-xs pt-1" style={{ color: valueOnly ? C.accent : C.cyan }}>
                {sortedContent.matches?.length ?? 0} partite — ordinate per {valueOnly
                  ? (vbSort === "prob" ? "probabilità di modello decrescente" : "edge decrescente")
                  : `probabilità ${PRED_FILTERS.find(f => f.key === predFilter)?.label}`}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 pb-12">

        {/* ─── Quick Criteria Strip ─────────────────────────────────────── */}
        {!loading && !error && enrichedLeagues?.length > 0 && (
          <div className="mb-5 p-3 space-y-3 animate-fade-in"
            style={{ background: C.surface, border: `1px solid ${C.border}` }}>
            {/* Preset chips + counter */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-data text-xs uppercase tracking-widest flex-shrink-0" style={{ color: C.muted }}>Filtra:</span>
              {[
                { key: "sicure",  label: "SICURE",  Icon: Shield,    color: C.accent },
                { key: "value",   label: "VALUE",   Icon: TrendingUp, color: C.accent },
                { key: "top5",    label: "TOP 5",   Icon: Trophy,    color: C.yellow },
                { key: "ora",     label: "ORE 2H",  Icon: Clock,     color: C.cyan },
              ].map(({ key, label, Icon, color }) => {
                const isActive = activePreset === key;
                return (
                  <button key={key} onClick={() => applyPreset(key)} data-testid={`preset-${key}`}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-heading font-bold uppercase transition-all"
                    style={{ background: isActive ? color : `${color}18`, color: isActive ? "#020202" : color, border: `1px solid ${isActive ? color : `${color}55`}` }}>
                    <Icon size={9} />{label}
                  </button>
                );
              })}
              <div className="ml-auto flex items-center gap-2">
                {(activePreset || minAff > 0 || minProb > 0) && (
                  <button onClick={resetQC} data-testid="qc-reset-btn"
                    className="flex items-center gap-1 px-2 py-1 text-xs transition-all"
                    style={{ color: C.muted, border: `1px solid ${C.border}` }}>
                    <X size={9} />Reset
                  </button>
                )}
                <span className="font-data text-xs font-bold tabular-nums px-2 py-0.5"
                  style={{ background: "rgba(6,182,212,0.08)", color: C.cyan, border: `1px solid rgba(6,182,212,0.2)` }}>
                  {qcFilteredLeagues.reduce((c, l) => c + l.matches.length, 0)} partite
                </span>
              </div>
            </div>
            {/* Sliders */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { label: "Affidabilità min", value: minAff, onChange: handleMinAff, color: C.cyan },
                { label: "TK Prob. min",     value: minProb, onChange: handleMinProb, color: C.yellow },
              ].map(({ label, value, onChange, color }) => (
                <div key={label} className="flex items-center gap-2">
                  <span className="font-data text-xs whitespace-nowrap" style={{ color: C.muted, minWidth: "7.5rem" }}>{label}</span>
                  <input type="range" min={0} max={100} step={5} value={value}
                    onChange={e => onChange(e.target.value)}
                    className="flex-1 cursor-pointer" style={{ accentColor: color, height: "3px" }} />
                  <span className="font-data text-xs font-bold tabular-nums w-9 text-right"
                    style={{ color: value > 0 ? color : C.muted }}>{value}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* ────────────────────────────────────────────────────────────────── */}
        {/* Match Juice — Top 5 del giorno (solo quando non filtrato per campionato specifico) */}
        {!loading && !error && !selectedLeagueId && topPicksJuice.length > 0 && (
          <div className="mb-6 animate-fade-in" data-testid="match-juice-section">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={15} style={{ color: "#FF6B35" }} />
              <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: C.text }}>
                Match Juice — Top {topPicksJuice.length} del Giorno
              </span>
              <span className="font-data text-[10px] px-2 py-0.5 uppercase tracking-widest"
                style={{ background: "rgba(255,107,53,0.12)", color: "#FF6B35", border: "1px solid rgba(255,107,53,0.3)" }}>
                Quota ≥ 1.25
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="match-juice-grid">
              {topPicksJuice.map((m, idx) => {
                const bet = m.juiceBet;
                const aff = m.pred?.affidabilita || {};
                const affColor = aff.level === "ALTA" ? C.green : aff.level === "BASSA" ? "#EF4444" : C.yellow;
                const juice = m.juice || 0;
                // Color-code the juice badge: hot 85+, value 70-84, safe <70
                const juiceColor = juice >= 85 ? "#FF6B35" : juice >= 70 ? C.yellow : C.cyan;
                const juiceGlow = juice >= 85 ? "rgba(255,107,53,0.35)" : juice >= 70 ? "rgba(245,158,11,0.3)" : "rgba(6,182,212,0.25)";
                const juiceLabel = juice >= 85 ? "🔥 HOT" : juice >= 70 ? "⚡ VALUE" : "🎯 SAFE";
                const odds = bet?.book_odds || (bet?.prob ? (100 / bet.prob) : null);
                return (
                  <button key={m.id} data-testid={`match-juice-${idx}`} onClick={() => goToMatch(m.id)}
                    className="text-left p-4 transition-all duration-200 group hover-shimmer relative overflow-hidden"
                    style={{
                      background: C.surface,
                      border: `1px solid ${C.border}`,
                      borderTop: `2px solid ${juiceColor}`,
                      boxShadow: `0 0 24px -10px ${juiceGlow}`,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = juiceColor; e.currentTarget.style.background = C.highlight; e.currentTarget.style.boxShadow = `0 0 28px -4px ${juiceGlow}`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.borderTopColor = juiceColor; e.currentTarget.style.background = C.surface; e.currentTarget.style.boxShadow = `0 0 24px -10px ${juiceGlow}`; }}>
                    {/* Top row: league + juice badge */}
                    <div className="flex items-center gap-1.5 mb-3">
                      {m.leagueFlag && <img src={m.leagueFlag} alt="" className="w-3 h-2 object-cover" onError={(e) => e.target.style.display = "none"} />}
                      <span className="font-data text-xs truncate" style={{ color: C.muted, fontSize: "0.62rem", maxWidth: 90 }}>{m.leagueName}</span>
                      <span className="ml-auto font-data text-[9px] font-bold px-1.5 py-0.5 uppercase tracking-wider"
                        style={{ background: `${juiceColor}1A`, color: juiceColor, border: `1px solid ${juiceColor}40` }}>
                        {juiceLabel}
                      </span>
                    </div>
                    {/* Team names */}
                    <div className="flex items-center gap-2 mb-1">
                      {m.home_team.logo && <img src={m.home_team.logo} alt="" className="w-5 h-5 object-contain" onError={(e) => e.target.style.display = "none"} />}
                      <span className="font-heading font-bold text-xs uppercase truncate" style={{ color: C.text }}>{m.home_team.name}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      {m.away_team.logo && <img src={m.away_team.logo} alt="" className="w-5 h-5 object-contain" onError={(e) => e.target.style.display = "none"} />}
                      <span className="font-heading font-bold text-xs uppercase truncate" style={{ color: C.text }}>{m.away_team.name}</span>
                    </div>
                    {/* Pick bet with odds */}
                    {bet && (
                      <div className="flex items-center justify-between pt-2" style={{ borderTop: `1px solid ${C.highlight}` }}>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <TrendingUp size={11} style={{ color: juiceColor, flexShrink: 0 }} />
                          <span className="font-data text-xs truncate" style={{ color: C.text, maxWidth: 100 }}>{bet.bet}</span>
                          {bet.has_value && (
                            <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm flex-shrink-0" style={{ background: C.accent }}>
                              <svg width="8" height="8" viewBox="0 0 9 9" fill="none"><path d="M1.5 4.5l2 2L7.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </span>
                          )}
                        </div>
                        {odds && (
                          <span className="font-data text-xs font-bold tabular-nums px-1.5 py-0.5 flex-shrink-0"
                            style={{ background: `${juiceColor}1F`, color: juiceColor, border: `1px solid ${juiceColor}40` }}>
                            {odds.toFixed(2)}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Bottom row: juice score + reliability */}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1.5">
                        <Shield size={10} style={{ color: affColor }} />
                        <span className="font-data text-xs" style={{ color: affColor }}>{aff.level || "-"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <LeagueSparkline sparkline={leagueSparklines[String(m.leagueId)]} />
                        <span className="font-data text-[10px] font-bold tabular-nums"
                          style={{ color: juiceColor, textShadow: `0 0 8px ${juiceGlow}` }}>
                          JUICE {juice}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}


        {loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <Loader2 size={32} className="animate-spin" style={{ color: C.accent }} />
            <span className="font-data text-xs uppercase tracking-widest" style={{ color: C.muted }}>Caricamento partite...</span>
          </div>
        )}
        {error && !loading && (
          <div className="text-center py-20">
            <p className="font-heading text-2xl mb-4" style={{ color: "#EF4444" }}>ERRORE</p>
            <p className="text-sm mb-6" style={{ color: C.muted }}>{error}</p>
            <button data-testid="retry-btn" onClick={() => setDate(new Date(date))}
              className="px-6 py-2 text-xs font-heading uppercase tracking-widest"
              style={{ background: C.accent, color: "#09090B", fontWeight: 700 }}>RIPROVA</button>
          </div>
        )}

        {/* FLAT SORTED VIEW */}
        {!loading && !error && sortedContent.type === "flat" && (
          <>
            <div className="mb-3 flex items-center gap-2 py-2" style={{ borderBottom: `1px solid ${C.border}` }}>
              <span className="font-data text-xs uppercase tracking-widest"
                style={{ color: sortedContent.isValue ? C.accent : sortedContent.isTime ? C.cyan : C.muted }}>
                {sortedContent.sortLabel
                  ? sortedContent.isValue
                    ? `Ordinate per ${vbSort === "prob" ? "probabilità di modello" : "edge value bet"} · ${sortedContent.matches.length} partite`
                    : sortedContent.isTime
                    ? `Tutte le partite ordinate per orario ${sortBy === "time_asc" ? "crescente ↑" : "decrescente ↓"} · ${sortedContent.matches.length} totali`
                    : `Ordinate per probabilità ${sortedContent.sortLabel} · ${sortedContent.matches.length} partite`
                  : `${sortedContent.matches.filter((m) => m.pred).length} analizzate · ${sortedContent.matches.filter((m) => !m.pred).length} non analizzate`}
              </span>
            </div>
            <div style={{ border: `1px solid ${C.border}` }}>
              {sortedContent.matches.map((m, j) => {
                const activePF = PRED_FILTERS.find((f) => f.key === predFilter);
                // Compute the metric shown in the card header (must match sort key)
                const marketProb = (() => {
                  if (sortedContent.isValue) {
                    const bets = m.pred?.recommended_bets || [];
                    const valueBets = bets.filter(b => b.has_value);
                    if (vbSort === "prob") {
                      const tkProb = m.pred?.target_kitano?.has_value ? (m.pred.target_kitano.prob || 0) : 0;
                      return Math.max(tkProb, valueBets.reduce((acc, b) => Math.max(acc, b.prob || 0), 0));
                    }
                    // Edge (default)
                    const tkEdge = m.pred?.target_kitano?.has_value ? (m.pred.target_kitano.value_pct || 0) : 0;
                    return Math.max(tkEdge, valueBets.reduce((acc, b) => Math.max(acc, b.value_pct || 0), 0));
                  }
                  return activePF?.sortField ? activePF.sortField(m.pred) : null;
                })();
                return (
                  <div key={m.id} className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: m.pred ? C.accent : C.highlight }} />
                    <div className="pl-2">
                      <div className="px-3 pt-1.5 pb-0.5 flex items-center gap-1.5">
                        {m.leagueFlag && <img src={m.leagueFlag} alt="" className="w-3 h-2 object-cover" onError={(e) => e.target.style.display = "none"} />}
                        <span className="font-data text-xs" style={{ color: C.muted, fontSize: "0.65rem" }}>{m.leagueName} · {m.leagueCountry}</span>
                        {sortedContent.isTime && m.date && (
                          <span className="font-data text-xs font-bold flex items-center gap-1 ml-auto"
                            style={{ color: C.cyan, fontSize: "0.65rem" }}>
                            <Clock size={9} />
                            {new Date(m.date).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        )}
                        {marketProb !== null && marketProb > 0 && !sortedContent.isCross && (
                          <span className="font-data text-xs font-bold ml-auto"
                            style={{ color: sortedContent.isValue ? C.accent : (marketProb > 65 ? C.green : marketProb > 50 ? C.yellow : C.muted) }}>
                            {sortedContent.isValue
                              ? (vbSort === "prob" ? `Prob: ${marketProb.toFixed(1)}%` : `Edge: +${marketProb.toFixed(1)}%`)
                              : activePF?.key === "gundam"
                              ? `Gundam: ${marketProb.toFixed(1)} pts`
                              : activePF?.dynamicLabel
                              ? `${activePF.dynamicLabel(m.pred)}: ${marketProb.toFixed(1)}%`
                              : `${activePF?.label}: ${marketProb.toFixed(1)}%`}
                          </span>
                        )}
                        {sortedContent.isCross && m._crossInfo && (
                          <div className="ml-auto flex items-center gap-1.5"
                            data-testid={`cross-badge-${m.id}`}>
                            {m._crossInfo.keys.map((k, idx) => {
                              const label = PRED_FILTERS.find((f) => f.key === k)?.label || k;
                              const p = m._crossInfo.probs[idx];
                              const tone = p > 65 ? C.green : p > 50 ? C.yellow : C.muted;
                              return (
                                <span key={k} className="font-data text-[10px] px-1.5 py-0.5 tabular-nums"
                                  style={{ background: `${tone}1A`, border: `1px solid ${tone}55`, color: tone }}>
                                  {label} {p.toFixed(0)}%
                                </span>
                              );
                            })}
                            <span className="font-data text-xs font-bold px-2 py-0.5 tabular-nums"
                              style={{ background: "rgba(167,139,250,0.15)", color: "#A78BFA", border: "1px solid rgba(167,139,250,0.5)" }}>
                              ⨉ {m._crossInfo.score.toFixed(1)}
                            </span>
                          </div>
                        )}
                        {!marketProb && m.pred && (
                          <span className="font-data text-xs" style={{ color: C.muted, fontSize: "0.65rem" }}>
                            · Aff. {m.pred.affidabilita?.level || m.pred.risk?.level || "-"} ({m.pred.affidabilita?.score ?? m.pred.risk?.score ?? 0}/100)
                          </span>
                        )}
                      </div>
                      <MatchCard match={m} isLast={j === sortedContent.matches.length - 1} predictionBadge={m.badge} predictionData={m.pred}
                        valueBadge={valueOnly && m._bestValue ? m._bestValue : null}
                        dnaBadge={sortBy === "dna" && typeof m.pred?.match_dna_score === "number"
                          ? { score: m.pred.match_dna_score, tier: m.pred.match_dna_tier, n: m.pred.match_dna_n }
                          : null}
                        profileScoreBadge={sortedContent.isProfile && m._profileScore != null && m._profileScore >= 0
                          ? { score: m._profileScore, color: getProfile(playProfile).color, label: getProfile(playProfile).short }
                          : null}
                        onClick={() => goToMatch(m.id)} />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* GROUPED VIEW */}
        {!loading && !error && sortedContent.type === "grouped" && (
          <>
            {sortedContent.leagues.length === 0 && (
              <div className="text-center py-20">
                <p className="font-heading text-3xl mb-3" style={{ color: C.muted }}>NESSUNA PARTITA</p>
                <p className="text-sm" style={{ color: "var(--bc-sub, #52525B)" }}>Nessuna partita trovata per questo filtro</p>
              </div>
            )}
            {sortedContent.leagues.map((league, i) => (
              <div key={league.id} className="mb-6 animate-fade-in" style={{ animationDelay: `${i * 0.04}s` }} data-testid={`league-group-${league.id}`}>
                <div className="flex items-center gap-3 px-4 py-2.5 mb-1"
                  style={{ background: "var(--bc-panel-elev, rgba(255,255,255,0.025))", borderLeft: `3px solid ${isFavorite(league.id) ? C.yellow : C.accent}` }}>
                  <div className="flex items-center gap-2">
                    {league.flag && <img src={league.flag} alt="" className="w-4 h-3 object-cover" onError={(e) => e.target.style.display = "none"} />}
                    {league.logo && <img src={league.logo} alt="" className="w-5 h-5 object-contain" onError={(e) => e.target.style.display = "none"} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-heading font-bold text-sm uppercase tracking-wider truncate block" style={{ color: C.text }}>{league.name}</span>
                    <span className="font-data text-xs" style={{ color: C.muted }}>{league.country}</span>
                  </div>
                  <span className="font-data text-xs px-2 py-0.5" style={{ background: "var(--bc-panel, var(--bc-panel, #09090B))", color: C.muted }}>{league.matches.length} GARE</span>
                  <button
                    data-testid={`fav-${league.id}`}
                    onClick={(e) => { e.stopPropagation(); toggleFavorite(league.id, league.name, league.country); }}
                    className="p-1.5 transition-all hover:scale-110"
                    title={isFavorite(league.id) ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}>
                    <Star size={14} fill={isFavorite(league.id) ? C.yellow : "none"}
                      style={{ color: isFavorite(league.id) ? C.yellow : C.muted }} />
                  </button>
                </div>
                <div style={{ border: `1px solid ${C.border}`, borderTop: "none" }}>
                  {league.matches.map((match, j) => (
                    <MatchCard key={match.id} match={match} isLast={j === league.matches.length - 1}
                      predictionBadge={match.badge} predictionData={match.pred}
                      onClick={() => goToMatch(match.id)} />
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
      {brainLabOpen && (
        <PublicBrainLabModal
          onClose={() => setBrainLabOpen(false)}
          onPresetAdded={reloadPresetsFromStorage}
        />
      )}
    </div>
  );
}
